/**
 * "View as": lets a sys_admin see the app exactly as another member does —
 * their role, their menus, their unread notes — without being able to change
 * anything while doing so.
 *
 * How it hangs together:
 * - The session lives in localStorage, so every tab of the admin's browser is
 *   in the same mode (a change in one tab reloads the others).
 * - getCurrentUser() in authService returns the viewed member while a session
 *   is active, so every screen that asks "who am I / what's my role" answers as
 *   that member without screen-by-screen changes.
 * - Writes are refused in three places:
 *     1. client: guardedWrites.js wraps every Firestore write and callable;
 *     2. Firestore rules: viewAsSessions/{adminUid} exists → writes denied;
 *     3. Cloud Functions: callables check the same doc before doing anything.
 *   The client check gives a clear message; the server checks are what
 *   actually guarantee nothing is changed.
 * - The activity log keeps recording under the admin's own identity, tagged
 *   with who they were viewing as.
 */

const STORAGE_KEY = 'btagms:viewAs';

export const VIEW_AS_READ_ONLY_CODE = 'view-as/read-only';

const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const write = (session) => {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: view-as cannot persist, which leaves the app in the
    // admin's own (normal) mode — the safe direction.
  }
};

/**
 * The active session for the given signed-in admin, or null.
 * @param {string} [realUid] - uid of the actually signed-in user
 * @returns {{adminUid, uid, email, displayName, role, startedAt}|null}
 */
export const getViewAs = (realUid) => {
  const session = read();
  if (!session) return null;
  if (realUid && session.adminUid !== realUid) return null;
  return session;
};

export const isViewingAs = () => getViewAs() !== null;

export const setViewAsSession = (session) => write(session);

export const clearViewAsSession = () => write(null);

/**
 * Throw if the app is in view-as mode. Called before every write.
 */
export const assertCanWrite = () => {
  const session = read();
  if (!session) return;
  const err = new Error(
    `Read-only: you are viewing the app as ${session.displayName || session.email}. Exit "View as" to make changes.`
  );
  err.code = VIEW_AS_READ_ONLY_CODE;
  throw err;
};

// Another tab started or ended a session: reload so this tab matches.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) window.location.reload();
  });
}

/**
 * For disabling write controls up front while viewing as someone, rather than
 * letting them be clicked and refused:
 *   const ro = readOnlyProps();
 *   <button disabled={saving || ro.disabled} title={ro.title || 'Save'}>
 * The mode can't change without a page reload, so reading it during render
 * is safe.
 */
export const READ_ONLY_TITLE = 'Read-only while viewing as another user';

export const readOnlyProps = () =>
  isViewingAs() ? { disabled: true, title: READ_ONLY_TITLE } : { disabled: false, title: undefined };
